import React, { FC, useEffect, useRef, useState } from 'react';
import { identifyComponent } from '../../util';
import css from './index.module.scss';

interface Size {
  width: number | undefined;
  height: number | undefined;
}

export interface SizerProps {
  children: (size: Size) => React.ReactNode;
}

export const Sizer: FC<SizerProps> = ({ children }) => {
  const container = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<Size>({ height: undefined, width: undefined });

  useEffect(() => {
    const el = container.current;
    if (!el) return;

    let frameID: number;
    const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      frameID = window.requestAnimationFrame(() => {
        for (const entry of entries) {
          if (entry.contentRect.height !== size.height || entry.contentRect.width !== size.width) {
            setSize({
              height: entry.contentRect.height,
              width: entry.contentRect.width,
            });
          }
        }
      });
    });

    observer.observe(el);

    return () => {
      observer.unobserve(el);
      window.cancelAnimationFrame(frameID);
    };
  }, [container.current]);

  return (
    <div data-component="Sizer" className={css.sizerRelative} {...identifyComponent('sizer')}>
      <div ref={container} className={css.sizerStretch}>
        {(size.height ?? 0) > 0 && children(size)}
      </div>
    </div>
  );
};
